/**
 * Portal payment methods (tracker item 45): cards on file (brand, last 4,
 * expiry only), add a card through the processor form, remove, and autopay.
 * Turning autopay on shows the exact consent wording from the server and sends
 * { accepted: true, version } of that wording, every time (the backend refuses
 * anything else); turning it off keeps the consent record as evidence.
 */
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../api/types';
import { usePortalApi, usePortal } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { EmptyBlock, FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { CardEntryForm } from '../../features/online-payments/CardEntryForm';
import { cardLabel, expiryLabel } from '../../features/online-payments/onlinePaymentsUi';
import { primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { describePortalFailure } from '../../utils/portal';
import { invoiceDate } from '../invoiceUi';

const dangerButtonClass =
  'min-h-[44px] rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400';
const actionClass = 'inline-flex min-h-[44px] items-center px-2 text-sm font-semibold text-blue-700 hover:underline disabled:text-gray-400';

export function PortalPaymentMethodsPage() {
  const api = usePortalApi();
  const { reportFailure } = usePortal();
  const load = useCallback(() => api.getPortalPaymentMethods(), [api]);
  const { data: cards, loading, failure, reload } = usePortalLoad(load, 'We could not load your payment methods.');
  const [adding, setAdding] = useState(false);
  const [addAutopay, setAddAutopay] = useState(false);
  const [consentFor, setConsentFor] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [removeFor, setRemoveFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(work: () => Promise<unknown>, fallback: string, success?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await reload();
      if (success) setNotice(success);
    } catch (cause) {
      const described = describePortalFailure(cause, fallback);
      setError(described.message);
      reportFailure(described);
    } finally {
      setBusy(false);
    }
  }

  if (loading && !cards) return <LoadingBlock label="Loading payment methods" />;
  if (failure) return <FailureBlock failure={failure} onRetry={() => void reload()} />;
  if (!cards) return null;

  const consent = cards.autopayConsent;
  const consentInput = (): types.AutopayConsentInput | undefined => (consent ? { accepted: true, version: consent.version } : undefined);
  const turnOn = (method: types.PortalPaymentMethod) => {
    setConsentFor(method.id);
    setAgreed(false);
    setError(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-gray-900">Payment methods</h1>
        {cards.available && !adding && (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setAdding(true); setAddAutopay(false); }}>Add card</button>
        )}
      </div>
      {!cards.available && cards.unavailableReason && <InlineAlert kind="info">{cards.unavailableReason}</InlineAlert>}
      {notice && <InlineAlert kind="success">{notice}</InlineAlert>}
      {error && <InlineAlert>{error}</InlineAlert>}

      {cards.methods.length === 0 && !adding ? (
        <EmptyBlock>You have no cards on file.</EmptyBlock>
      ) : (
        <ul className="space-y-3" aria-label="Cards on file">
          {cards.methods.map((m) => (
            <li key={m.id} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" data-testid="payment-method">
              <p className="font-semibold text-gray-900">
                {cardLabel(m)}
                {m.isDefault && <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Default</span>}
                {m.autopayEnabled && <span className="ml-2 rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">Autopay on</span>}
                {m.status === 'EXPIRED' && <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Expired</span>}
              </p>
              <p className="text-xs text-gray-500">
                {expiryLabel(m) && `Expires ${expiryLabel(m)}`}
                {m.autopayConsentAt && ` · Autopay agreed ${invoiceDate(m.autopayConsentAt)}`}
              </p>
              {m.status === 'ACTIVE' && (
                <div className="mt-1 flex flex-wrap gap-x-2">
                  {m.autopayEnabled ? (
                    <button type="button" className={actionClass} disabled={busy}
                      onClick={() => void run(() => api.setPortalAutopay(m.id, { enabled: false }), 'Could not turn autopay off', 'Autopay is off for this card.')}>
                      Turn autopay off
                    </button>
                  ) : (
                    cards.available && <button type="button" className={actionClass} disabled={busy} onClick={() => turnOn(m)}>Turn autopay on</button>
                  )}
                  <button type="button" className={`${actionClass} !text-red-600`} disabled={busy} onClick={() => setRemoveFor(m.id)}>Remove</button>
                </div>
              )}
              {consentFor === m.id && consent && (
                <div role="alertdialog" aria-label="Autopay consent" className="mt-3 space-y-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm">
                  <p className="font-medium">Please read and agree to turn autopay on:</p>
                  <blockquote className="border-l-4 border-blue-300 pl-3 text-gray-800" data-consent-version={consent.version}>{consent.text}</blockquote>
                  <label className="flex min-h-[44px] items-start gap-3">
                    <input type="checkbox" className="mt-0.5 h-5 w-5" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    <span>I agree</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" className={primaryButtonClass} disabled={busy || !agreed}
                      onClick={() => void run(async () => {
                        await api.setPortalAutopay(m.id, { enabled: true, autopayConsent: consentInput() });
                        setConsentFor(null);
                      }, 'Could not turn autopay on', 'Autopay is on for this card.')}>
                      Turn autopay on
                    </button>
                    <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConsentFor(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {removeFor === m.id && (
                <div role="alertdialog" aria-label="Remove card" className="mt-3 space-y-3 rounded border border-red-200 bg-red-50 p-3 text-sm">
                  <p>Remove {cardLabel(m)}? It is deleted at the card processor{m.autopayEnabled ? ' and autopay stops' : ''}.</p>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" className={dangerButtonClass} disabled={busy}
                      onClick={() => void run(async () => { await api.removePortalPaymentMethod(m.id); setRemoveFor(null); }, 'Could not remove the card', 'The card was removed.')}>
                      Remove card
                    </button>
                    <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setRemoveFor(null)}>Keep</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && cards.clientConfig && (
        <section aria-labelledby="add-card-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <h2 id="add-card-heading" className="mb-2 font-semibold">Add a card</h2>
          <CardEntryForm
            clientConfig={cards.clientConfig}
            submitLabel="Save card"
            onCancel={() => setAdding(false)}
            onToken={async (sourceToken) => {
              await api.addPortalPaymentMethod({ sourceId: sourceToken, autopayConsent: addAutopay ? consentInput() : undefined });
              setAdding(false);
              setNotice('Your card was saved.');
              await reload();
            }}
          >
            {consent && (
              <label className="flex min-h-[44px] items-start gap-3 text-sm">
                <input type="checkbox" className="mt-0.5 h-5 w-5" checked={addAutopay} onChange={(e) => setAddAutopay(e.target.checked)} />
                <span>
                  <span className="font-medium">Turn autopay on for this card (I agree to this wording)</span>
                  <span className="block text-xs text-gray-600" data-consent-version={consent.version}>{consent.text}</span>
                </span>
              </label>
            )}
          </CardEntryForm>
        </section>
      )}
      <Link to="/portal/invoices" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">&larr; Invoices</Link>
    </div>
  );
}
